import { useCallback, useEffect, useState } from 'react';
import { apiService, SpeechSegment } from '../services/api.service';

/**
 * Задача H1: переключатель «Без слов-паразитов» в транскрипте — перезагружает сегменты
 * с `?clean=true` (инлайн-чистка на бэкенде, БД не меняется). Состояние переключателя
 * намеренно не персистится (дефолт — выключено при каждой загрузке страницы).
 */
export function useCleanTranscript(meetingId: string | undefined, fallbackSegments: SpeechSegment[]) {
  const [enabled, setEnabled] = useState(false);
  const [cleanSegments, setCleanSegments] = useState<SpeechSegment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Смена встречи — сброс, чтобы не показывать «чистые» сегменты чужой записи.
  // Дефолт переключателя всегда выключен (состояние намеренно не персистится).
  useEffect(() => {
    setEnabled(false);
    setCleanSegments(null);
    setError('');
  }, [meetingId]);

  const toggle = useCallback(async () => {
    if (!meetingId || loading) return;
    if (enabled) {
      setEnabled(false);
      return;
    }
    if (cleanSegments) {
      setEnabled(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getMeetingSegments(meetingId, { clean: true });
      setCleanSegments(res.segments || []);
      setEnabled(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить чистые сегменты');
    } finally {
      setLoading(false);
    }
  }, [meetingId, enabled, cleanSegments, loading]);

  const segments = enabled && cleanSegments ? cleanSegments : fallbackSegments;

  return { segments, enabled, toggle, loading, error };
}
